import { useLayoutEffect, useRef, useState, useMemo } from "react";

const canvas = document.createElement("canvas");
const ctx = canvas.getContext("2d");

/**
 * Custom hook that truncates filenames to fit within their container width.
 * 
 * This hook automatically measures the available width of the container element
 * and truncates the filename with an ellipsis (...) when it doesn't fit.
 * It uses a ResizeObserver to respond to container size changes and recalculates
 * the truncated filename accordingly.
 * 
 * @param filename - The original filename to be truncated (e.g., "very-long-document-name.pdf")
 * @returns Object containing:
 *   - containerRef: Ref to attach to the container element that constrains the text width
 *   - displayName: The truncated filename that fits within the container (e.g., "very-long-d...pdf")
 * 
 * @example
 * const { containerRef, displayName } = useTruncatedFilename("very-long-filename.pdf");
 * 
 * return (
 *   <div ref={containerRef} style={{ width: '200px' }}>
 *     {displayName}
 *   </div>
 * );
 * 
 * @notes
 * - Uses canvas text measurement for accurate width calculation
 * - Automatically responds to container resize events
 * - Preserves file extension in truncated output
 * - Returns original filename if it fits within available space
 */
export const useTruncatedFilename = (filename: string) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [displayName, setDisplayName] = useState(filename);

    const { name, ext } = useMemo(() => {
        const dot = filename.lastIndexOf(".");
        return dot !== -1
            ? { name: filename.slice(0, dot), ext: filename.slice(dot) }
            : { name: filename, ext: "" };
    }, [filename]);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el || !ctx) return;

        const style = window.getComputedStyle(el);
        ctx.font = style.font;

        const truncate = (width: number) => {
            if (ctx.measureText(filename).width <= width) return filename;

            const suffix = `...${ext}`;

            let left = 0;
            let right = name.length;
            let result = suffix;

            while (left <= right) {
                const mid = Math.floor((left + right) / 2);

                const candidate = name.slice(0, mid) + suffix;

                if (ctx.measureText(candidate).width <= width) {
                    result = candidate;
                    left = mid + 1; 
                } else {
                    right = mid - 1; 
                }
            }

            return result;
        };

        const observer = new ResizeObserver(([entry]) => {
            const width = entry.contentRect.width - 8;
            const next = truncate(width);
            setDisplayName((prev) => (prev === next ? prev : next));
        });

        observer.observe(el);

        return () => observer.disconnect();
    }, [filename, name, ext]);

    return { containerRef, displayName };
};